import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Calendar, CheckCircle2, ArrowLeft, Utensils } from "lucide-react";

const bookingSchema = z.object({
  name: z.string()
    .trim()
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be less than 100 characters")
    .regex(/^[a-zA-Z\s'-]+$/, "Name contains invalid characters"),
  email: z.string()
    .trim()
    .email("Invalid email address")
    .max(255, "Email too long"),
  phone: z.string()
    .trim()
    .min(10, "Phone number must be at least 10 digits")
    .max(20, "Phone number too long")
    .regex(/^[+]?[\d\s()-]+$/, "Invalid phone number format"),
  intake_month: z.string()
    .min(1, "Please select an intake month"),
  plan_type: z.string()
    .min(1, "Please select a plan type"),
  university: z.string()
    .trim()
    .max(200, "University name too long")
    .optional(),
  arrival_date: z.string()
    .optional(),
  meal_addon: z.string()
    .optional(),
  special_requests: z.string()
    .trim()
    .max(1000, "Special requests must be less than 1000 characters")
    .optional()
});

const Booking = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const preselectedPlan = searchParams.get("plan") || "";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    intake_month: "",
    plan_type: preselectedPlan,
    university: "",
    arrival_date: "",
    meal_addon: "",
    special_requests: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate input
      const validatedData = bookingSchema.parse(formData);
      
      const { error } = await supabase.from("bookings").insert([{
        name: validatedData.name,
        email: validatedData.email,
        phone: validatedData.phone,
        intake_month: validatedData.intake_month,
        plan_type: validatedData.plan_type,
        university: validatedData.university || null,
        arrival_date: validatedData.arrival_date || null,
        meal_addon: validatedData.meal_addon || null,
        special_requests: validatedData.special_requests || null
      }]);

      if (error) throw error;

      toast({
        title: "Booking Submitted Successfully! 🎉",
        description: "We'll contact you within 24 hours to confirm your booking.",
      });

      setFormData({
        name: "",
        email: "",
        phone: "",
        intake_month: "",
        plan_type: "",
        university: "",
        arrival_date: "",
        meal_addon: "",
        special_requests: "",
      });

      setTimeout(() => navigate("/"), 2000);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Submission Failed",
          description: "Please try again or contact us directly via WhatsApp.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const planPrices = {
    "smooth-landing": "$229AUD",
    "settle-smart": "$449AUD",
    "premium-anchor": "$649AUD",
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-8">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12 animate-fade-in">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
                Secure Your Spot
              </h1>
              <p className="text-xl text-muted-foreground">
                Complete your booking and let us handle the rest
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Booking Form */}
              <Card className="md:col-span-2 animate-scale-in">
                <CardHeader>
                  <CardTitle>Booking Details</CardTitle>
                  <CardDescription>
                    Fill in your information and we'll get everything ready for your arrival
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+91 XXXXX XXXXX"
                        />
                      </div>
                      <div>
                        <Label htmlFor="intake_month">Intake Month *</Label>
                        <Select
                          required
                          value={formData.intake_month}
                          onValueChange={(value) => setFormData({ ...formData, intake_month: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select month" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="February 2026">February 2026</SelectItem>
                            <SelectItem value="July 2026">July 2026</SelectItem>
                            <SelectItem value="September 2026">September 2026</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="plan_type">Service Plan *</Label>
                      <Select
                        required
                        value={formData.plan_type}
                        onValueChange={(value) => setFormData({ ...formData, plan_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a plan" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="smooth-landing">Smooth Landing - $328.90 AUD (incl. GST)</SelectItem>
                          <SelectItem value="settle-smart">Settle Smart - $548.90 AUD (incl. GST) - Most Popular</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="university">University</Label>
                        <Input
                          id="university"
                          value={formData.university}
                          onChange={(e) => setFormData({ ...formData, university: e.target.value })}
                          placeholder="University of Melbourne"
                        />
                      </div>
                      <div>
                        <Label htmlFor="arrival_date">Expected Arrival Date</Label>
                        <Input
                          id="arrival_date"
                          type="date"
                          value={formData.arrival_date}
                          onChange={(e) => setFormData({ ...formData, arrival_date: e.target.value })}
                        />
                      </div>
                    </div>

                    {/* Add-ons Section */}
                    <div className="space-y-4 pt-6 border-t border-border">
                      <div className="flex items-center gap-2 mb-4">
                        <Utensils className="h-5 w-5 text-secondary" />
                        <Label className="text-base font-semibold">Optional Add-Ons</Label>
                      </div>
                      
                      <div>
                        <Label htmlFor="meal_addon">Indian Veg Meal Plans</Label>
                        <Select
                          value={formData.meal_addon}
                          onValueChange={(value) => setFormData({ ...formData, meal_addon: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="No meal add-on (you can add later)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No meal add-on</SelectItem>
                            <SelectItem value="3-days">🥣 3 Days - $75.90 AUD (incl. GST) - Taste of Home Starter Pack</SelectItem>
                            <SelectItem value="5-days">🍛 5 Days - $108.90 AUD (incl. GST) - Comfort Pack (Most Popular)</SelectItem>
                            <SelectItem value="7-days">🍲 7 Days - $141.90 AUD (incl. GST) - Full Week Pack</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-2">
                          Fresh, home-cooked vegetarian Indian meals delivered during your first week
                        </p>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="special_requests">Special Requests or Questions</Label>
                      <Textarea
                        id="special_requests"
                        value={formData.special_requests}
                        onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                        placeholder="Any dietary requirements, accessibility needs, or specific preferences?"
                        rows={4}
                      />
                    </div>

                    {/* Payment Terms */}
                    <Card className="bg-accent/5 border-accent/20">
                      <CardContent className="pt-6">
                        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                          <CheckCircle2 className="h-5 w-5 text-secondary" />
                          Payment Terms & Information
                        </h3>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                          <li className="flex items-start gap-2">
                            <span className="text-secondary font-bold">•</span>
                            <span><strong className="text-foreground">All prices are GST inclusive</strong> - No hidden charges</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-secondary font-bold">•</span>
                            <span><strong className="text-foreground">Non-refundable booking amount</strong> is required to secure your spot</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-secondary font-bold">•</span>
                            <span><strong className="text-foreground">Balance payable in Australia</strong> on arrival by card, cash, or payment link</span>
                          </li>
                        </ul>
                        <p className="text-xs text-muted-foreground mt-4 pt-4 border-t border-border/50">
                          By submitting this booking, you agree to our Terms & Conditions and Privacy Policy
                        </p>
                      </CardContent>
                    </Card>

                    <Button type="submit" size="lg" className="w-full" disabled={loading}>
                      {loading ? "Processing..." : "Confirm Booking"}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Summary Card */}
              <div className="space-y-6">
                <Card className="animate-fade-in" style={{ animationDelay: "100ms" }}>
                  <CardHeader>
                    <CardTitle>What Happens Next?</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Instant Confirmation</p>
                        <p className="text-sm text-muted-foreground">
                          Receive booking confirmation via email
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Personal Coordinator</p>
                        <p className="text-sm text-muted-foreground">
                          Get assigned a dedicated team member
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Pre-Arrival Planning</p>
                        <p className="text-sm text-muted-foreground">
                          Start planning your smooth transition
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-primary animate-fade-in" style={{ animationDelay: "200ms" }}>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Calendar className="h-5 w-5" />
                      <h3 className="font-semibold">Limited Spots Available</h3>
                    </div>
                    <p className="text-sm opacity-90 mb-4">
                      We maintain quality by limiting bookings per intake. Secure your spot today!
                    </p>
                    <div className="text-2xl font-bold">Only 5 spots left</div>
                    <p className="text-sm opacity-75 mt-1">for February 2026 intake</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Booking;
